/**
 * The action permissions the background-job screens gate on.
 *
 * BACKGROUND_JOB.READ is not returned: GroupScreen already hides a tab the user
 * cannot read, so a tab that renders has it by definition.
 *
 * BACKGROUND_JOB_RUN.READ *is* returned, and the difference matters. It is a
 * SEPARATE grant, and a tab rendering only proves the one permission GroupScreen
 * gated it on. The dashboard is gated on BACKGROUND_JOB.READ and also lists live
 * runs — so it renders happily for someone who cannot read runs at all, and every
 * run request on it 403s. Anything calling a run endpoint has to check this itself.
 */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { BACKGROUND_JOB_PERMISSIONS } from './shared';

export interface JobPermissions {
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  /** independent of update — re-running is not rewriting */
  canExecute: boolean;
  /** independent again — pausing a schedule needs neither create nor update */
  canToggle: boolean;
  /** BACKGROUND_JOB_RUN.READ — separate from BACKGROUND_JOB.READ; see the header */
  canReadRuns: boolean;
}

export function useJobPermissions(): JobPermissions {
  const keys = useSelector((s: { auth?: { permissions?: string[] } }) => s.auth?.permissions || []);
  return useMemo(() => {
    const set = new Set(keys);
    return {
      canCreate: set.has(BACKGROUND_JOB_PERMISSIONS.create),
      canUpdate: set.has(BACKGROUND_JOB_PERMISSIONS.update),
      canDelete: set.has(BACKGROUND_JOB_PERMISSIONS.delete),
      canExecute: set.has(BACKGROUND_JOB_PERMISSIONS.execute),
      canToggle: set.has(BACKGROUND_JOB_PERMISSIONS.toggle),
      canReadRuns: set.has(BACKGROUND_JOB_PERMISSIONS.runRead),
    };
  }, [keys]);
}
